import {
  Box,
  Slider,
  styled,
  Switch,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import {
  CameraSelection,
  selectBackendColorCorrection,
  selectBackendIntendedColorCorrection,
  selectCameraSelection,
  selectDepthMapOpacity,
  selectFrontendColorCorrection,
  selectFrontendColorCorrectionProcessDarkImagesOverride,
  selectImageBrightness,
  selectImageResolution,
  selectShowDepthMap,
  setBackendColorCorrection,
  setBackendIntendedColorCorrection,
  setCameraSelection,
  setDepthMapOpacity,
  setFrontendColorCorrection,
  setFrontendColorCorrectionProcessDarkImagesOverride,
  setImageBrightness,
  setImageResolution,
  setShowDepthMap,
} from './imageSlice';
const imageQualities = [
  {
    value: 0,
    label: 'SD',
  },
  {
    value: 1,
    label: 'HD',
  },
  {
    value: 2,
    label: 'FHD',
  },
  {
    value: 3,
    label: '2K',
  },
  {
    value: 4,
    label: '4K',
  },
  {
    value: 5,
    label: '8K',
  },
];
const qualities = [480, 720, 1080, 1440, 2160, 4320];

const calculateValue = (value: number) => {
  if (value in qualities) {
    return qualities[value];
  }

  return qualities[0];
};

interface Props {
  isTooltipOpen: boolean;
}

const StyledBox = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '2rem',
  marginBottom: '0.2rem',
  marginTop: '0.2rem',
});

export const ImageSettingsPopup: React.FC<Props> = ({ isTooltipOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const imageBrightness = useAppSelector(selectImageBrightness);
  const imageResolution = useAppSelector(selectImageResolution);
  const cameraSelection = useAppSelector(selectCameraSelection);
  const showDepthMap = useAppSelector(selectShowDepthMap);
  const depthMapOpacity = useAppSelector(selectDepthMapOpacity);
  const frontendColorCorrection = useAppSelector(selectFrontendColorCorrection);
  const backendColorCorrection = useAppSelector(selectBackendColorCorrection);
  const backendIntendedColorCorrection = useAppSelector(
    selectBackendIntendedColorCorrection
  );
  const frontendColorCorrectionProcessDarkImagesOverride = useAppSelector(
    selectFrontendColorCorrectionProcessDarkImagesOverride
  );

  const [isTooltipOpenLocal, setIsTooltipOpenLocal] = useState(false);

  // If the tooltip is open in the parent, delay the local open state to allow the slider to hide it's own value tooltip until the parent tooltip is fully animated open
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTooltipOpenLocal(isTooltipOpen);
    }, 500);
    return () => clearTimeout(timeout);
  }, [isTooltipOpen]);

  const labelStyles: SxProps = {
    color: theme.colors.darkTheme.grey.muted,
    mb: '0.2rem',
    textAlign: 'center',
  };

  const handleCameraSelectionToggleClick = (
    _event: React.MouseEvent<HTMLElement>,
    newSelection: CameraSelection
  ) => {
    dispatch(setCameraSelection(newSelection));
    if (newSelection === 'right') {
      dispatch(setBackendColorCorrection(false));
    } else {
      if (backendColorCorrection !== backendIntendedColorCorrection)
        dispatch(setBackendColorCorrection(backendIntendedColorCorrection));
    }
  };

  const handleShowFrontendColorCorrectionClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setFrontendColorCorrection(event.target.checked));
  };

  const handleFrontendColorCorrectionProcessDarkImagesOverride = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      setFrontendColorCorrectionProcessDarkImagesOverride(event.target.checked)
    );
  };

  const handleShowBackendColorCorrectionClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setBackendIntendedColorCorrection(event.target.checked));
    dispatch(setBackendColorCorrection(event.target.checked));
  };

  const handleShowDepthMapToggleClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setShowDepthMap(event.target.checked));
  };

  const { ColorCorrectionToggle } = useFeatureFlags();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: '1rem',
        minWidth: '250px',
      }}
    >
      <DiagnosticFeature>
        <StyledBox>
          <Typography variant='body2' sx={{ ...labelStyles }}>
            {t('Camera', { ns: 'ranch-view' })}
          </Typography>
          <ToggleButtonGroup
            value={cameraSelection}
            exclusive
            onChange={handleCameraSelectionToggleClick}
            aria-label='Camera Selection Left / Right'
          >
            <ToggleButton value={'left' as CameraSelection}>
              {t('Left')}
            </ToggleButton>
            <ToggleButton value={'right' as CameraSelection}>
              {t('Right')}
            </ToggleButton>
          </ToggleButtonGroup>
        </StyledBox>
      </DiagnosticFeature>

      {ColorCorrectionToggle && (
        <>
          <StyledBox>
            <Typography variant='body2' sx={{ ...labelStyles }}>
              {t('Backend Color Correction', { ns: 'ranch-view' })}
            </Typography>
            <Switch
              checked={backendColorCorrection}
              onChange={handleShowBackendColorCorrectionClick}
              slotProps={{ input: { 'aria-label': 'Show Color Correction' } }}
              color='primary'
              disabled={cameraSelection === 'right'}
            />
          </StyledBox>
          <StyledBox>
            <Typography variant='body2' sx={{ ...labelStyles }}>
              {t('Frontend Color Correction', { ns: 'ranch-view' })}
            </Typography>
            <Switch
              checked={frontendColorCorrection}
              onChange={handleShowFrontendColorCorrectionClick}
              slotProps={{
                input: { 'aria-label': 'Show Frontend Color Correction' },
              }}
              color='primary'
            />
          </StyledBox>
          <StyledBox>
            <Typography variant='body2' sx={{ ...labelStyles }}>
              {t('Force Color Correction')}
            </Typography>
            <Switch
              checked={frontendColorCorrectionProcessDarkImagesOverride}
              onChange={handleFrontendColorCorrectionProcessDarkImagesOverride}
              slotProps={{
                input: { 'aria-label': 'Force Color Correction' },
              }}
              color='primary'
            />
          </StyledBox>
        </>
      )}
      <DiagnosticFeature>
        <StyledBox>
          <Typography variant='body2' sx={{ ...labelStyles }}>
            {t('Show Depth Map', { ns: 'ranch-view' })}
          </Typography>
          <Switch
            checked={showDepthMap}
            onChange={handleShowDepthMapToggleClick}
            slotProps={{ input: { 'aria-label': 'Show Depth Map' } }}
            color='primary'
          />
        </StyledBox>
        <Typography variant='body2' sx={{ ...labelStyles }}>
          {t('Depth Map Opacity', { ns: 'ranch-view' })}
        </Typography>
        <Slider
          defaultValue={0}
          value={depthMapOpacity}
          min={0}
          max={100}
          valueLabelFormat={(val) => `${val} %`}
          aria-label='Depth Map Opacity'
          valueLabelDisplay='auto'
          onChange={(_e, value) =>
            dispatch(setDepthMapOpacity(value as number))
          }
        />
      </DiagnosticFeature>
      <Typography variant='body2' sx={{ ...labelStyles }}>
        {t('brightness', { ns: 'ranch-view' })}
      </Typography>
      <Slider
        defaultValue={0}
        value={imageBrightness}
        min={-100}
        max={100}
        valueLabelFormat={(val) => `${val} %`}
        aria-label='Default'
        valueLabelDisplay={isTooltipOpenLocal ? 'auto' : 'off'}
        onChange={(_e, value) => dispatch(setImageBrightness(value as number))}
      />
      <Typography variant='body2' sx={{ ...labelStyles, mt: '0.5rem' }}>
        {t('resolution', { ns: 'ranch-view' })}
      </Typography>
      <Slider
        defaultValue={2}
        value={qualities.findIndex((val) => val === imageResolution)}
        marks={imageQualities}
        min={0}
        max={5}
        step={null}
        scale={calculateValue}
        valueLabelFormat={(val) => `${val} p`}
        aria-label='Default'
        valueLabelDisplay={isTooltipOpenLocal ? 'auto' : 'off'}
        onChange={(_e, value) =>
          dispatch(setImageResolution(calculateValue(value as number)))
        }
      />
    </Box>
  );
};
